import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, Button } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import useMediaQueryHook from "@hooks/useMediaQueryHook";
import stepBack from "@assets/images/stepsBack.jpg";
import { useTranslation } from "react-i18next";
import { getQuizData } from "api/quizGame";
import {
  formatDate,
  formatDateObject,
  parseDate,
} from "@utils/dateFormater";
// Import needed utility for date comparison
import { differenceInDays, isAfter, isBefore, isToday } from "date-fns";
import LoaderFilter from "@components/common/LoaderFilter";

const DashboardCharts = ({ graphData, setGraphData }) => {
  const { isMobile } = useMediaQueryHook();
  const { t } = useTranslation();

  // Helper to parse dates from graphData with proper format (dd-MM-yyyy)
  const parseGraphDates = () => {
    const defaultStart = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const defaultEnd = new Date();

    if (!graphData) {
      return {
        startDate: defaultStart,
        endDate: defaultEnd,
      };
    }

    try {
      // Parse dates from the format "06-11-2025" (dd-MM-yyyy)
      const startDate = graphData.start_date
        ? parseDate(graphData.start_date, "dd-MM-yyyy")
        : defaultStart;

      const endDate = graphData.end_date
        ? parseDate(graphData.end_date, "dd-MM-yyyy")
        : defaultEnd;

      return { startDate, endDate };
    } catch (error) {
      console.error("Error parsing dates:", error);
      return {
        startDate: defaultStart,
        endDate: defaultEnd,
      };
    }
  };

  // State for date range - initialize from graphData with proper parsing
  const [dateRange, setDateRange] = React.useState(parseGraphDates());
  // Separate error state for start and end date
  const [dateErrors, setDateErrors] = React.useState({
    startDate: "",
    endDate: "",
    global: "", // For errors that affect the pair (e.g., range)
  });
  const [isLoading, setIsLoading] = React.useState(false);

  // Update dateRange when graphData changes
  React.useEffect(() => {
    if (graphData) {
      setDateRange(parseGraphDates());
    }
  }, [graphData]);

  // Process chart data with proper Y-axis scaling
  const chartData =
    graphData?.graph_data?.map((item) => ({
      // Keep date format as is for XAxis to use, or use a shorter format
      name: item.name,
      points: item.points ? Number(item.points) : 0,
      originalDate: item.name,
    })) || [];

  const totalPoints = graphData?.total_points || 0;

  // Calculate Y-axis domain (kept as is, it's correct)
  const calculateYAxisDomain = (data) => {
    if (!data || data.length === 0) return [0, 100];
    const validPoints = data
      .map((item) => item.points)
      .filter(
        (points) =>
          points !== null &&
          points !== undefined &&
          !isNaN(points) &&
          points > 0
      );
    if (validPoints.length === 0) return [0, 100];

    const maxPoints = Math.max(...validPoints);
    const minPoints = Math.min(...validPoints);

    const maxDomain = Math.ceil(maxPoints / 10) * 10;
    const minDomain = Math.max(0, Math.floor(minPoints / 10) * 10);

    const range = maxDomain - minDomain;
    if (range < 40) {
      const additional = Math.ceil((40 - range) / 2);
      return [Math.max(0, minDomain - additional), maxDomain + additional];
    }

    return [minDomain, maxDomain];
  };

  const yAxisDomain = calculateYAxisDomain(chartData);

  const handleApplyDateRangeWithDates = async (startDate, endDate) => {
    setIsLoading(true);

    try {
      // Format dates for API
      const startDateFormatted = formatDateObject(startDate);
      const endDateFormatted = formatDateObject(endDate);

      const response = await getQuizData({
        start_date: startDateFormatted,
        end_date: endDateFormatted,
      });

      setGraphData(response?.weekly_graph_data);
    } catch (error) {
      console.log("Failed to get quiz data:", error);
      setDateErrors((prev) => ({
        ...prev,
        global: "Failed to load data for selected dates",
      }));
    } finally {
      setIsLoading(false);
    }
  };


  // Custom Y-axis tick formatter (kept as is)
  const yAxisTickFormatter = (value) => {
    return value % 10 === 0 ? value : "";
  };

  // Custom tooltip formatter (kept as is)
  const customTooltipFormatter = (value, name) => {
    if (value === 0 || value === null) return ["No points", "Score"];
    return [`${value} points`, "Score"];
  };

  // Calculate days selected
  const daysSelected =
    dateRange.startDate && dateRange.endDate
      ? differenceInDays(dateRange.endDate, dateRange.startDate) + 1
      : 0;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div
        className="p-6 rounded-xl text-white bg-cover"
        style={{ backgroundImage: `url(${stepBack})` }}
      >
        <div className="flex flex-col gap-4 mb-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h4 className="text-xl font-bold font-[Lato] text-white">
              {t("quizGame.Your Score Progress")}
            </h4>
          </div>

          {/* --- Date Filter & Controls Container (Styled for Grouping) --- */}

          <LoaderFilter
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onDateChange={(date, type) => {
              // Handle individual date updates if needed
              if (date && type === "startDate") {
                setDateRange((prev) => ({ ...prev, startDate: date }));
              } else if (date && type === "endDate") {
                setDateRange((prev) => ({ ...prev, endDate: date }));
              }
            }}
            onApplyFilter={(start, end) => {
              // Trigger data fetch with the new dates
              handleApplyDateRangeWithDates(start, end);
            }}
            loading={isLoading}
            dateErrors={dateErrors}
            daysSelected={daysSelected}
            MAX_DAYS_RANGE={15}
          />
          {/* --- End Date Filter & Controls Container --- */}
        </div>

        {chartData?.length > 0 ? (
          <div className="bg-white rounded-2xl">
            <div className="relative p-2">
              {/* Scrollable container for the chart */}
              <div className="overflow-x-auto">
                {/* minWidth ensures the chart content forces the scrollbar */}
                <div
                  style={{ minWidth: chartData.length > 7 ? "1000px" : "100%" }}
                >
                  <ResponsiveContainer
                    width="100%"
                    height={300}
                    // minWidth is passed to the parent div, not the container, but setting it here doesn't hurt
                    minWidth={chartData.length > 7 ? 1000 : undefined}
                  >
                    <LineChart
                      data={chartData}
                      margin={
                        isMobile
                          ? { top: 5, right: 10, left: 0, bottom: 5 }
                          : { top: 5, right: 10, left: 0, bottom: 5 }
                      }
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#D6D6D6"
                      />
                      <XAxis
                        dataKey="name"
                        axisLine={false}
                        tickLine={false}
                        tick={{
                          fill: "#6B7280",
                          fontSize: isMobile ? 10 : 11,
                        }}
                        interval={0}
                        minTickGap={isMobile ? 15 : 20}
                        padding={{ left: 15, right: 15 }}
                        height={isMobile ? 70 : 50}
                        tickMargin={15}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        domain={yAxisDomain}
                        tick={{
                          fill: "#6B7280",
                          fontSize: isMobile ? 10 : 12,
                        }}
                        tickFormatter={yAxisTickFormatter}
                        interval={0}
                        width={40}
                      />
                      <Tooltip
                        formatter={customTooltipFormatter}
                        labelFormatter={(label) => `Date: ${label}`}
                        contentStyle={{
                          borderRadius: "8px",
                          backgroundColor: "#fff",
                          border: "1px solid #e0e0e0",
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="points"
                        stroke="#3B82F6"
                        strokeWidth={3}
                        dot={{
                          r: isMobile ? 3 : 4,
                          fill: "#3B82F6",
                          strokeWidth: 2,
                        }}
                        activeDot={{ r: isMobile ? 5 : 6, fill: "#3B82F6" }}
                        connectNulls={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Total Points Display */}
              {/* {totalPoints > 0 && (
                <div className="absolute top-4 right-4">
                  <Card className="text-center py-2 px-3 border border-[#E0E0E0] !shadow-sm">
                    <div className="text-xs text-[#636363] mb-1">
                      Total Points
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-3 h-3 bg-[#0575e6] rounded-[3px]"></div>
                      <p className="text-[14px] text-[#636363] font-semibold">
                        {totalPoints}
                      </p>
                    </div>
                  </Card>
                </div>
              )} */}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-[300px] bg-white rounded-xl">
            <div className="text-center text-gray-500">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-gray-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
              <p className="text-[14px] font-[500] mb-1">
                {t("No Data Available")}
              </p>
              <p className="text-[12px] text-gray-400 max-w-md">
                {t(
                  "Your quiz progress will appear here once you start playing. Complete a quiz to begin tracking your performance and progress over time."
                )}
              </p>
            </div>
          </div>
        )}
      </div>
    </LocalizationProvider>
  );
};

export default DashboardCharts;
